import { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  MapPin, User, Mail, Phone, Loader2, Search,
  AlertCircle, CheckCircle, ExternalLink, Home, Ruler, Layers, Tag, X
} from "lucide-react";
import { SIDING_TYPES, getSidingUpcharge, isInServiceArea } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerInfoStep({
  customerInfo,
  setCustomerInfo,
  propertyDetails,
  setPropertyDetails,
  sidingType,
  setSidingType,
  getFullAddress,
  onPropertyImagesLoad,
  promoCode,
  setPromoCode,
  promoDiscount,
  setPromoDiscount,
  promoValidated,
  setPromoValidated,
  promoMessage,
  setPromoMessage,
  promoDiscountType,
  setPromoDiscountType,
  promoDiscountValue,
  setPromoDiscountValue,
  subtotal
}) {
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Section refs for auto-scrolling
  const phoneSectionRef = useRef(null);
  const phoneInputRef = useRef(null);
  const addressSectionRef = useRef(null);
  const propertyDetailsSectionRef = useRef(null);
  const optInsSectionRef = useRef(null);

  // Local state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [propertyLookupResult, setPropertyLookupResult] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [serviceAreaError, setServiceAreaError] = useState(false);
  const [addressValidation, setAddressValidation] = useState({
    validated: false,
    valid: false,
    message: "",
    suggestions: [],
  });
  
  // Track scroll progress to avoid repeated scrolls
  const scrollProgressRef = useRef(0);
  // Track if phone field was manually focused by user (not programmatic)
  const phoneWasTouchedRef = useRef(false);

  // Auto-scroll helper function
  const scrollToSection = useCallback((ref, progressLevel) => {
    if (ref?.current && scrollProgressRef.current < progressLevel) {
      scrollProgressRef.current = progressLevel;
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, []);

  // Handler for email field blur - scroll to phone if name and email are valid
  const handleEmailBlur = () => {
    const emailLooksComplete = customerInfo.email && 
      customerInfo.email.includes('@') && 
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(customerInfo.email);
    
    if (customerInfo.first_name && customerInfo.last_name && emailLooksComplete && scrollProgressRef.current < 1) {
      scrollProgressRef.current = 1;
      // Scroll to phone input and focus it
      setTimeout(() => {
        phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          phoneInputRef.current?.focus();
        }, 300);
      }, 100);
    }
  };

  // Auto-scroll when address is complete -> scroll to property details
  useEffect(() => {
    if (customerInfo.street_address && customerInfo.city && customerInfo.zip_code?.length === 5 && !serviceAreaError) {
      scrollToSection(propertyDetailsSectionRef, 3);
    }
  }, [customerInfo.street_address, customerInfo.city, customerInfo.zip_code, serviceAreaError, scrollToSection]);

  // Auto-scroll when property details are filled -> scroll to opt-ins
  useEffect(() => {
    if (propertyDetails.square_footage && propertyDetails.stories && sidingType) {
      scrollToSection(optInsSectionRef, 4);
    }
  }, [propertyDetails.square_footage, propertyDetails.stories, sidingType, scrollToSection]);

  // Handler for phone field focus - mark as touched when user interacts
  const handlePhoneFocus = () => {
    phoneWasTouchedRef.current = true;
  };

  // Handler for phone field blur - scroll to address if user skips phone
  const handlePhoneBlur = () => {
    // Only scroll to address section if user actually touched the phone field
    if (phoneWasTouchedRef.current && scrollProgressRef.current >= 1 && scrollProgressRef.current < 2) {
      scrollProgressRef.current = 2;
      // Scroll to address section with center positioning
      setTimeout(() => {
        addressSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          addressInputRef.current?.focus();
        }, 300);
      }, 100);
    }
  };

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoMessage("");
      setPromoValidated(false);
      setPromoDiscount(0);
      setPromoDiscountType(null);
      setPromoDiscountValue(0);
      return;
    }

    if (!customerInfo.email) {
      toast.error("Please enter your email first to validate promo code");
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim().toUpperCase(),
        email: customerInfo.email,
        subtotal: subtotal || 100 // Use current subtotal or default
      });

      const { valid, message, discount_amount, discount_type, discount_value } = response.data;
      
      // Create a better message for percentage codes
      let displayMessage = message;
      if (valid && discount_type === 'percentage') {
        displayMessage = `Promo code applied! ${discount_value}% off your order`;
      }
      
      setPromoMessage(displayMessage);
      setPromoValidated(valid);
      setPromoDiscount(valid ? discount_amount : 0);
      
      // Store the discount type and raw value for percentage recalculation
      if (valid) {
        setPromoDiscountType(discount_type);
        setPromoDiscountValue(discount_value || discount_amount); // Use discount_value from API
      } else {
        setPromoDiscountType(null);
        setPromoDiscountValue(0);
      }
      
      if (valid) {
        toast.success(displayMessage);
      } else {
        toast.error(message);
      }
    } catch (error) {
      setPromoMessage("Failed to validate promo code");
      setPromoValidated(false);
      setPromoDiscount(0);
      setPromoDiscountType(null);
      setPromoDiscountValue(0);
      toast.error("Failed to validate promo code");
    } finally {
      setValidatingPromo(false);
    }
  };

  // Clear promo code
  const clearPromoCode = () => {
    setPromoCode("");
    setPromoMessage("");
    setPromoValidated(false);
    setPromoDiscount(0);
    setPromoDiscountType(null);
    setPromoDiscountValue(0);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Address autocomplete search
  const searchAddresses = async (query) => {
    if (query.length < 5) {
      setAddressSuggestions([]);
      return;
    }
    
    setSearchingAddress(true);
    try {
      const res = await axios.get(`${API}/address/autocomplete`, { params: { query } });
      if (res.data.error) {
        // Rate limited or error - silently continue, user can type manually
        setAddressSuggestions([]);
      } else {
        setAddressSuggestions(res.data.suggestions || []);
        if (res.data.suggestions?.length > 0) {
          setShowSuggestions(true);
        }
      }
    } catch {
      // Address search failed - user can type manually
      setAddressSuggestions([]);
    } finally {
      setSearchingAddress(false);
    }
  };

  // Debounced address search - increased debounce time to avoid rate limiting
  const handleStreetAddressChange = (value) => {
    setCustomerInfo({ ...customerInfo, street_address: value });
    const timer = setTimeout(() => {
      // Only search after 5+ characters to reduce API calls
      if (value.length >= 5) {
        searchAddresses(value);
      }
    }, 600);  // Increased from 300ms to 600ms
    return () => clearTimeout(timer);
  };

  // Select address from suggestions
  const selectAddressSuggestion = async (suggestion) => {
    // Check if address is in service area
    if (suggestion.in_service_area === false) {
      toast.error("Sorry, this address is outside our service area. We currently serve DuPage County and surrounding areas in Illinois.");
      setAddressValidation({
        validated: true,
        valid: false,
        message: "Service not available at this location",
        suggestions: ["We currently serve DuPage County and surrounding areas in Illinois."],
      });
      return;
    }
    
    setCustomerInfo({
      ...customerInfo,
      street_address: suggestion.street_address,
      city: suggestion.city,
      state: suggestion.state || "IL",
      zip_code: suggestion.zip_code,
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);
    
    // Update property coordinates if available
    if (suggestion.latitude && suggestion.longitude) {
      setPropertyDetails(prev => ({
        ...prev,
        latitude: suggestion.latitude,
        longitude: suggestion.longitude,
      }));
    }
    
    setAddressValidation({
      validated: true,
      valid: true,
      message: "Address verified",
      suggestions: [],
    });
    toast.success("Address auto-filled!");
  };

  // Validate address fields
  const validateAddressFields = () => {
    const issues = [];
    if (!customerInfo.street_address || customerInfo.street_address.length < 5) {
      issues.push("Street address is required");
    } else if (!/^\d+/.test(customerInfo.street_address.trim())) {
      issues.push("Street address should start with a number (e.g., '123 Main St')");
    }
    if (!customerInfo.city || customerInfo.city.length < 2) {
      issues.push("City is required");
    }
    if (!customerInfo.state) {
      issues.push("State is required");
    }
    if (!customerInfo.zip_code) {
      issues.push("ZIP code is required");
    } else if (!/^\d{5}(-\d{4})?$/.test(customerInfo.zip_code)) {
      issues.push("ZIP code must be 5 digits (e.g., '60540')");
    }
    return { valid: issues.length === 0, issues };
  };

  const handleAddressBlur = () => {
    const validation = validateAddressFields();
    setAddressValidation({
      validated: true,
      valid: validation.valid,
      message: validation.valid ? "Address looks complete" : "Please complete all address fields",
      suggestions: validation.issues,
    });
  };

  // Look up property data
  const lookupProperty = async () => {
    if (!customerInfo.street_address || !customerInfo.zip_code) {
      toast.error("Please enter street address and ZIP code first");
      return;
    }

    setLookingUp(true);
    setPropertyLookupResult(null);

    try {
      const fullAddress = getFullAddress();
      const res = await axios.post(`${API}/property/lookup`, {
        address: fullAddress,
        zipcode: customerInfo.zip_code
      });

      setPropertyLookupResult(res.data);

      if (res.data.success && res.data.square_footage) {
        setPropertyDetails(prev => ({
          ...prev,
          square_footage: res.data.square_footage,
          verified_sqft: res.data.square_footage,
          sqft_source: res.data.source,
          latitude: res.data.latitude,
          longitude: res.data.longitude,
          bedrooms: res.data.bedrooms,
          bathrooms: res.data.bathrooms,
          year_built: res.data.year_built,
        }));
        toast.success(`Property found! ${res.data.square_footage.toLocaleString()} sq ft`);
        
        if (res.data.latitude && res.data.longitude) {
          onPropertyImagesLoad(res.data.latitude, res.data.longitude);
        }
      } else if (res.data.latitude) {
        setPropertyDetails(prev => ({
          ...prev,
          latitude: res.data.latitude,
          longitude: res.data.longitude,
          sqft_source: "user_input",
        }));
        toast.info("Address verified. Please confirm square footage.");
        
        if (res.data.latitude && res.data.longitude) {
          onPropertyImagesLoad(res.data.latitude, res.data.longitude);
        }
      } else {
        toast.warning("Property not found. Please enter details manually.");
      }
    } catch (err) {
      toast.error("Failed to look up property");
    } finally {
      setLookingUp(false);
    }
  };

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 3) {
      // Just digits
    } else if (value.length <= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    }
    setCustomerInfo({ ...customerInfo, phone: value });
  };

  return (
    <div className="max-w-xl mx-auto animate-fade-in-up" data-testid="step-1-form">
      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
        Let's get started
      </h2>
      <p className="text-slate-600 mb-8">Tell us about yourself and your property</p>

      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-6">
          {/* First Name and Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name" className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                First Name
              </Label>
              <Input
                id="first_name"
                placeholder="John"
                value={customerInfo.first_name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, first_name: e.target.value })}
                className="input-field"
                data-testid="input-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name" className="flex items-center gap-2 invisible sm:visible">
                <User className="w-4 h-4 text-slate-400 invisible" />
                Last Name
              </Label>
              <Input
                id="last_name"
                placeholder="Smith"
                value={customerInfo.last_name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, last_name: e.target.value })}
                className="input-field"
                data-testid="input-last-name"
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              value={customerInfo.email}
              onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
              onBlur={handleEmailBlur}
              className="input-field"
              data-testid="input-email"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2" ref={phoneSectionRef}>
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              Phone Number {!customerInfo.sms_opt_in && <span className="text-slate-400 font-normal">(Optional)</span>}
              {customerInfo.sms_opt_in && <span className="text-red-500">*</span>}
            </Label>
            <Input
              ref={phoneInputRef}
              id="phone"
              type="tel"
              placeholder="(630) 555-1234"
              value={customerInfo.phone}
              onChange={handlePhoneChange}
              onFocus={handlePhoneFocus}
              onBlur={handlePhoneBlur}
              className="input-field"
              data-testid="input-phone"
              maxLength={14}
            />
            {customerInfo.sms_opt_in && !customerInfo.phone && (
              <p className="text-xs text-red-500">Phone number is required for SMS notifications</p>
            )}
          </div>

          {/* Property Address Fields */}
          <div className="space-y-4" ref={addressSectionRef}>
            <Label className="flex items-center gap-2 text-base font-medium">
              <MapPin className="w-4 h-4 text-slate-400" />
              Property Address
            </Label>
            
            {/* Street Address with Autocomplete */}
            <div className="space-y-2 relative">
              <Label htmlFor="street_address" className="text-sm text-slate-600">Street Address</Label>
              <div className="relative">
                <Input
                  ref={addressInputRef}
                  id="street_address"
                  placeholder="Start typing address..."
                  value={customerInfo.street_address}
                  onChange={(e) => handleStreetAddressChange(e.target.value)}
                  onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={handleAddressBlur}
                  className="input-field pr-10"
                  data-testid="input-street-address"
                  autoComplete="off"
                />
                {searchingAddress && (
                  <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                )}
              </div>
              
              {/* Address Suggestions Dropdown */}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto"
                >
                  {addressSuggestions.map((suggestion) => (
                    <button
                      key={`${suggestion.street_address}-${suggestion.zip_code}`}
                      type="button"
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors"
                      onClick={() => selectAddressSuggestion(suggestion)}
                      data-testid={`address-suggestion-${suggestion.zip_code}`}
                    >
                      <p className="font-medium text-slate-900">{suggestion.street_address}</p>
                      <p className="text-sm text-slate-500">
                        {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Type your address and select from suggestions to auto-fill city, state, and ZIP
              </p>
            </div>
            
            {/* City, State, ZIP in a row */}
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-3 space-y-2">
                <Label htmlFor="city" className="text-sm text-slate-600">City</Label>
                <Input
                  id="city"
                  placeholder="Naperville"
                  value={customerInfo.city}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                  onBlur={handleAddressBlur}
                  className={`input-field ${customerInfo.city ? 'bg-slate-50' : ''}`}
                  data-testid="input-city"
                />
              </div>
              <div className="col-span-1 space-y-2">
                <Label htmlFor="state" className="text-sm text-slate-600">State</Label>
                <Select
                  value={customerInfo.state}
                  onValueChange={(val) => setCustomerInfo({ ...customerInfo, state: val })}
                  disabled={true}
                >
                  <SelectTrigger className="input-field bg-slate-100" data-testid="input-state">
                    <SelectValue placeholder="IL" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IL">IL - Illinois</SelectItem>
                    <SelectItem value="IN" disabled className="text-slate-400">IN - Indiana (Not Available)</SelectItem>
                    <SelectItem value="WI" disabled className="text-slate-400">WI - Wisconsin (Not Available)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Service available in Illinois only</p>
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="zip_code" className="text-sm text-slate-600">ZIP Code</Label>
                <Input
                  id="zip_code"
                  placeholder="60540"
                  value={customerInfo.zip_code}
                  onChange={(e) => {
                    const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setCustomerInfo({ ...customerInfo, zip_code: zip });
                    // Check service area when ZIP is complete
                    if (zip.length === 5) {
                      if (!isInServiceArea(zip)) {
                        setServiceAreaError(true);
                        toast.error("Sorry, this ZIP code is outside our service area.");
                      } else {
                        setServiceAreaError(false);
                      }
                    } else {
                      setServiceAreaError(false);
                    }
                  }}
                  onBlur={handleAddressBlur}
                  className={`input-field ${customerInfo.zip_code ? 'bg-slate-50' : ''} ${serviceAreaError ? 'border-red-500' : ''}`}
                  maxLength={5}
                  data-testid="input-zip"
                />
                {serviceAreaError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Outside service area
                  </p>
                )}
              </div>
            </div>

            {/* Property Lookup Button */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={lookupProperty}
                disabled={lookingUp || !customerInfo.street_address || !customerInfo.zip_code}
                className="flex-1"
                data-testid="lookup-property-btn"
              >
                {lookingUp ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Look Up Property Details
              </Button>
            </div>
            
            {/* Address Validation Feedback */}
            {addressValidation.validated && !addressValidation.valid && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{addressValidation.message}</p>
                    {addressValidation.suggestions.length > 0 && (
                      <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                        {addressValidation.suggestions.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {addressValidation.validated && addressValidation.valid && (
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <p className="text-sm text-emerald-800">{addressValidation.message}</p>
                </div>
              </div>
            )}
          </div>

          {/* Property Lookup Result */}
          {propertyLookupResult && (
            <div className={`p-4 rounded-lg border ${
              propertyLookupResult.success && propertyLookupResult.square_footage
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-start gap-3">
                {propertyLookupResult.success && propertyLookupResult.square_footage ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${
                    propertyLookupResult.success && propertyLookupResult.square_footage
                      ? 'text-emerald-800'
                      : 'text-yellow-800'
                  }`}>
                    {propertyLookupResult.message}
                  </p>
                  {propertyLookupResult.square_footage && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Square Footage:</span>
                        <span className="ml-2 font-semibold">{propertyLookupResult.square_footage?.toLocaleString()}</span>
                      </div>
                      {propertyLookupResult.bedrooms && (
                        <div>
                          <span className="text-slate-500">Bedrooms:</span>
                          <span className="ml-2 font-semibold">{propertyLookupResult.bedrooms}</span>
                        </div>
                      )}
                      {propertyLookupResult.bathrooms && (
                        <div>
                          <span className="text-slate-500">Bathrooms:</span>
                          <span className="ml-2 font-semibold">{propertyLookupResult.bathrooms}</span>
                        </div>
                      )}
                      {propertyLookupResult.year_built && (
                        <div>
                          <span className="text-slate-500">Year Built:</span>
                          <span className="ml-2 font-semibold">{propertyLookupResult.year_built}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {propertyLookupResult.source && (
                    <Badge variant="secondary" className="mt-2 text-xs">
                      Source: {propertyLookupResult.source}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Property Details - Required Radio Button Selections */}
          <div className="space-y-6 pt-4 border-t" ref={propertyDetailsSectionRef}>
            <div className="flex items-center gap-2 mb-2">
              <Home className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-slate-900">Property Details</h3>
              <span className="text-xs text-red-500 ml-1">* Required</span>
            </div>

            {/* Square Footage - Radio Buttons */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-medium">
                <Ruler className="w-4 h-4 text-slate-500" />
                House Size (Square Footage) *
              </Label>
              <RadioGroup
                value={propertyDetails.square_footage?.toString() || ""}
                onValueChange={(val) => setPropertyDetails({ ...propertyDetails, square_footage: parseInt(val) })}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                data-testid="radio-sqft"
              >
                {[
                  { value: "1000", label: "Under 1,500 sq ft", desc: "Small home" },
                  { value: "2000", label: "1,500 - 2,500 sq ft", desc: "Average home" },
                  { value: "3000", label: "2,500 - 3,500 sq ft", desc: "Large home" },
                  { value: "4000", label: "3,500 - 4,500 sq ft", desc: "Very large" },
                  { value: "5000", label: "Over 4,500 sq ft", desc: "Estate" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      propertyDetails.square_footage?.toString() === option.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`sqft-option-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium text-slate-900">{option.label}</p>
                      <p className="text-xs text-slate-500">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              <a
                href={`https://www.zillow.com/homes/${encodeURIComponent(
                  `${customerInfo.street_address} ${customerInfo.city} ${customerInfo.state} ${customerInfo.zip_code}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
                data-testid="zillow-lookup-link"
              >
                <ExternalLink className="w-3 h-3" />
                Don't know your sq ft? Look up on Zillow
              </a>
            </div>

            {/* Number of Stories - Radio Buttons */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-medium">
                <Layers className="w-4 h-4 text-slate-500" />
                Number of Stories *
              </Label>
              <RadioGroup
                value={propertyDetails.stories?.toString() || ""}
                onValueChange={(val) => setPropertyDetails({ ...propertyDetails, stories: parseInt(val) })}
                className="flex flex-wrap gap-3"
                data-testid="radio-stories"
              >
                {[
                  { value: "1", label: "1 Story", desc: "Ranch/Single level" },
                  { value: "2", label: "2 Stories", desc: "Two-story home" },
                  { value: "3", label: "3+ Stories", desc: "Multi-level" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all min-w-[140px] ${
                      propertyDetails.stories?.toString() === option.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`stories-option-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium text-slate-900">{option.label}</p>
                      <p className="text-xs text-slate-500">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Siding Type - Radio Buttons */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-medium">
                <Home className="w-4 h-4 text-slate-500" />
                Siding Material *
              </Label>
              <RadioGroup
                value={sidingType || ""}
                onValueChange={(val) => setSidingType(val)}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                data-testid="radio-siding"
              >
                {SIDING_TYPES.map((type) => {
                  const sqft = propertyDetails.square_footage || 2000;
                  const upcharge = getSidingUpcharge(type.value, sqft);
                  return (
                    <label
                      key={type.value}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        sidingType === type.value
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      data-testid={`siding-option-${type.value}`}
                    >
                      <RadioGroupItem value={type.value} />
                      <div>
                        <p className="font-medium text-slate-900">{type.label}</p>
                        {type.sublabel && (
                          <p className="text-xs text-slate-600">{type.sublabel}</p>
                        )}
                        {upcharge > 0 ? (
                          <p className="text-xs text-amber-600">+${upcharge} upcharge</p>
                        ) : (
                          <p className="text-xs text-slate-500">No upcharge</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
              <p className="text-xs text-slate-500">Stucco and wood siding require additional cleaning time</p>
            </div>
          </div>

          {/* Promo Code */}
          <div className="border-t pt-6 mt-6">
            <Label className="flex items-center gap-2 text-base font-medium mb-3">
              <Tag className="w-4 h-4 text-slate-400" />
              Have a Promo Code?
            </Label>
            {/* Avion tweak: items-center vertically centers the Apply button against the taller input */}
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    if (promoValidated) {
                      setPromoValidated(false);
                      setPromoMessage("");
                      setPromoDiscount(0);
                    }
                  }}
                  className={`input-field ${promoValidated ? 'border-green-500 bg-green-50' : ''}`}
                  data-testid="input-promo-code"
                  disabled={promoValidated}
                />
                {promoValidated && (
                  <button
                    onClick={clearPromoCode}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                type="button"
                onClick={validatePromoCode}
                disabled={validatingPromo || promoValidated || !promoCode.trim()}
                variant={promoValidated ? "outline" : "default"}
                className={promoValidated ? "border-green-500 text-green-600" : ""}
                data-testid="apply-promo-btn"
              >
                {validatingPromo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : promoValidated ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Applied
                  </>
                ) : (
                  "Apply"
                )}
              </Button>
            </div>
            {promoMessage && (
              <p className={`text-sm mt-2 ${promoValidated ? 'text-green-600' : 'text-red-500'}`}>
                {promoValidated && <CheckCircle className="w-4 h-4 inline mr-1" />}
                {!promoValidated && promoMessage && <AlertCircle className="w-4 h-4 inline mr-1" />}
                {promoMessage}
              </p>
            )}
            {promoValidated && promoDiscount > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">
                  {promoDiscountType === 'percentage' 
                    ? `🎉 You'll get ${promoDiscountValue}% off with code ${promoCode}!`
                    : `🎉 You'll save $${promoDiscount.toFixed(2)} with code ${promoCode}!`
                  }
                </p>
              </div>
            )}
          </div>

          {/* Marketing Opt-ins */}
          <div className="border-t pt-6 mt-6" ref={optInsSectionRef}>
            <p className="text-sm font-medium text-slate-700 mb-4">Stay updated with specials & tips</p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="email_opt_in"
                  checked={customerInfo.email_opt_in}
                  onCheckedChange={(checked) => setCustomerInfo({ ...customerInfo, email_opt_in: checked })}
                  data-testid="checkbox-email-optin"
                />
                <Label htmlFor="email_opt_in" className="text-sm text-slate-600 cursor-pointer">
                  Send me monthly specials and cleaning tips via email
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="sms_opt_in"
                  checked={customerInfo.sms_opt_in}
                  onCheckedChange={(checked) => setCustomerInfo({ ...customerInfo, sms_opt_in: checked })}
                  data-testid="checkbox-sms-optin"
                />
                <Label htmlFor="sms_opt_in" className="text-sm text-slate-600 cursor-pointer">
                  Send me text messages about exclusive deals
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
